import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ProfilePreviewWrapper } from "../Dashboard/ProfilePage/style";
import { getStorage, setStorage } from "../../Utils/common";
import Swal from 'sweetalert2';
import './CIBILRequest.css';

// Fixed demo OTP. Nothing is sent via SMS — this is a UI placeholder only.
const DEMO_OTP = "7678";

function CIBILRequest() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // "form" | "otp"
  const [formData, setFormData] = useState({
    fullName: "",
    mobileNumber: "",
    email: "",
    address: "",
    panNumber: "",
    dateOfBirth: "",
    consent: false
  });

  const [otpValue, setOtpValue] = useState(["", "", "", ""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const userData = {
      fullName: getStorage("user_name") || "",
      mobileNumber: getStorage("mobile") || getStorage("user_mobile") || "",
      email: getStorage("user_email") || "",
      panNumber: getStorage("pancard") || "",
    };
    setFormData(prev => ({ ...prev, ...userData }));
  }, []);

  // DEMO ONLY: returns a fixed, hardcoded score instead of calling a real bureau.
  const generateDemoScore = () => {
    const DEMO_SCORE = 700; // change here if you want a different fixed demo number

    return {
      score: DEMO_SCORE,
      isDemo: true,
      lastUpdated: new Date().toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric'
      })
    };
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!formData.fullName || !formData.mobileNumber || !formData.panNumber) {
        throw new Error("Please fill all required fields");
      }
      if (!formData.consent) {
        throw new Error("Please accept the consent to view your demo score");
      }

      await new Promise(resolve => setTimeout(resolve, 800));

      // Move to OTP step instead of submitting immediately
      setStep("otp");
    } catch (error) {
      await Swal.fire({
        title: 'Error!',
        text: error.message || "Failed to submit request. Please try again.",
        icon: 'error',
        confirmButtonColor: '#105840',
        confirmButtonText: 'Try Again',
      });
      setSubmitStatus({ type: "error", message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const updated = [...otpValue];
    updated[index] = value;
    setOtpValue(updated);

    // auto-focus next box
    if (value && index < 3) {
      const next = document.getElementById(`otp-box-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const entered = otpValue.join("");
      if (entered.length < 4) {
        throw new Error("Please enter the 4-digit demo OTP");
      }
      if (entered !== DEMO_OTP) {
        throw new Error(`Incorrect OTP.`);
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      const scoreData = generateDemoScore();
      setStorage("bureau_score", JSON.stringify(scoreData));

      await Swal.fire({
        title: 'Bureau Score Ready',
        // text: 'This is a demo score for UI preview — not a real bureau report.',
        icon: 'success',
        confirmButtonColor: '#105840',
        confirmButtonText: 'View Score',
        timer: 3000,
        timerProgressBar: true,
        allowOutsideClick: false,
      });

      navigate('/my-dashboard/bureau-score', { state: { bureauScore: scoreData } });
    } catch (error) {
      await Swal.fire({
        title: 'Error!',
        text: error.message,
        icon: 'error',
        confirmButtonColor: '#105840',
        confirmButtonText: 'Try Again',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === "otp") {
      setStep("form");
    } else {
      navigate(-1);
    }
  };

  return (
    <ProfilePreviewWrapper>
      <div className="cibil-request-container">
        <div className="cibil-request-header">
          <button className="back-button" onClick={handleBack}>
            ← Back
          </button>
          <h1 className="cibil-request-title">Bureau Score Request</h1>
          {/* <span className="demo-mode-badge">DEMO / TEST MODE — No real bureau or SMS is contacted</span> */}
        </div>

        {submitStatus && (
          <div className={`status-message ${submitStatus.type}`}>
            {submitStatus.message}
          </div>
        )}

        {step === "form" && (
          <form className="cibil-request-form" onSubmit={handleFormSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="fullName">Full Name <span className="required">*</span></label>
                <input
                  type="text" id="fullName" name="fullName"
                  value={formData.fullName} onChange={handleChange}
                  placeholder="Enter your full name" required disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mobileNumber">Mobile Number <span className="required">*</span></label>
                <input
                  type="tel" id="mobileNumber" name="mobileNumber"
                  value={formData.mobileNumber} onChange={handleChange}
                  placeholder="Enter 10-digit mobile number" pattern="[0-9]{10}"
                  required disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address <span className="required">*</span></label>
                <input
                  type="email" id="email" name="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="Enter your email address" required disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="panNumber">PAN Number <span className="required">*</span></label>
                <input
                  type="text" id="panNumber" name="panNumber"
                  value={formData.panNumber} onChange={handleChange}
                  placeholder="Enter PAN card number" pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                  required disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth</label>
                <input
                  type="date" id="dateOfBirth" name="dateOfBirth"
                  value={formData.dateOfBirth} onChange={handleChange} disabled={isSubmitting}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="address">Residential Address <span className="required">*</span></label>
                <textarea
                  id="address" name="address" value={formData.address}
                  onChange={handleChange} placeholder="Enter your complete residential address"
                  rows="3" required disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="consent-section">
              <label className="consent-label">
                <input
                  type="checkbox" name="consent" checked={formData.consent}
                  onChange={handleChange} disabled={isSubmitting}
                />
                <span>
                 I understand and consent to the retrieval of my bureau score from authorized bureaus for application verification.
                  <span className="required"> *</span>
                </span>
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={handleBack} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? "Processing..." : "Continue"}
              </button>
            </div>
          </form>
        )}

        {step === "otp" && (
          <form className="cibil-request-form otp-form" onSubmit={handleOtpSubmit}>
            
            <p className="otp-target">Enter OTP sent to {formData.mobileNumber}</p>

            <div className="otp-input-row">
              {otpValue.map((digit, idx) => (
                <input
                  key={idx}
                  id={`otp-box-${idx}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="otp-box"
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  disabled={isSubmitting}
                />
              ))}
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={handleBack} disabled={isSubmitting}>
                Back
              </button>
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? "Verifying..." : "Verify & View Score"}
              </button>
            </div>
          </form>
        )}
      </div>
    </ProfilePreviewWrapper>
  );
}

export default CIBILRequest;